// Palette-swap the Kenney walking kid into a per-avatar recoloured spritesheet, so each
// agent has a REAL 4-direction walking body whose skin / hair / shirt match its Appearance.
//
// Why a repacked sheet: the source sprite reuses colours across regions (the dark brown is
// BOTH hair and the whole-body outline; one orange is hair-light AND skin-shadow AND shoes).
// A pure colour-key swap would bleed, so we recolour shirt+skin by colour (they're isolated)
// and hair by colour ONLY in the top 6 rows (where the head always is). We copy the 12 walk
// frames into a compact 3×4 sheet and recolour each, giving a clean spritesheet + walk mapping.

import { type Appearance, HAIR_HEX, SKIN_HEX, SHIRT_HEX } from '../net/protocol'
import { PLAYER_TEXTURE, PLAYER_WALK } from '../Scenes/GameScene'

const DIRS = ['right', 'down', 'up', 'left'] as const
// compact layout: 3 columns (standing/leftFoot/rightFoot) × 4 rows (one per direction)
const SRC_FRAMES = DIRS.flatMap(d => [PLAYER_WALK[d].standing, PLAYER_WALK[d].leftFoot, PLAYER_WALK[d].rightFoot])
export const BODY_WALK = Object.fromEntries(
    DIRS.map((d, i) => [d, { standing: i * 3, leftFoot: i * 3 + 1, rightFoot: i * 3 + 2 }]),
) as Record<typeof DIRS[number], { standing: number; leftFoot: number; rightFoot: number }>

export function bodyKey(id: string): string { return `body-${id}` }

type RGB = [number, number, number]
function hexRGB(h: string): RGB { return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)] }
function mul([r, g, b]: RGB, f: number): RGB { return [Math.min(255, r * f) | 0, Math.min(255, g * f) | 0, Math.min(255, b * f) | 0] }

const HAIR_ROWS = 6 // head/hair lives in the top 6 rows of each 16px frame

/** Build/refresh the `body-<id>` canvas spritesheet recoloured for this appearance. Returns the key. */
export function buildRecoloredBody(scene: Phaser.Scene, id: string, ap: Appearance): string {
    const tex = scene.textures.get(PLAYER_TEXTURE)
    const srcImg = tex.getSourceImage() as CanvasImageSource

    const skin = hexRGB(SKIN_HEX[ap.skinColor])
    const hair = hexRGB(HAIR_HEX[ap.hairColor])
    const shirt = hexRGB(SHIRT_HEX[ap.shirtColor])
    const skinSh = mul(skin, 0.7)

    // source colour → target replacement (isolated regions)
    const SHIRT: Record<string, RGB> = { '66,163,121': shirt, '54,144,105': mul(shirt, 0.78) }
    const SKIN: Record<string, RGB> = {
        '255,201,153': mul(skin, 1.18), '255,200,161': mul(skin, 1.18),
        '241,176,137': skin, '226,151,121': mul(skin, 0.86),
    }
    const HAIR: Record<string, RGB> = { '141,82,67': mul(hair, 0.6), '197,118,82': mul(hair, 0.85), '220,134,82': mul(hair, 1.12) }
    const SKIN_SHADOW_SRC = '220,134,82' // the shared orange, treated as skin-shadow below the hair line

    // PAD between frames so scaling never bleeds a neighbour's edge row into the next frame.
    const PAD = 2, STRIDE = 16 + PAD
    const canvas = document.createElement('canvas')
    canvas.width = 3 * STRIDE; canvas.height = 4 * STRIDE
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!

    for (let idx = 0; idx < 12; idx++) {
        const fr: any = tex.frames[SRC_FRAMES[idx]]
        const col = idx % 3, row = (idx / 3) | 0, cx = col * STRIDE, cy = row * STRIDE
        ctx.clearRect(cx, cy, 16, 16)
        ctx.drawImage(srcImg, fr.cutX, fr.cutY, 16, 16, cx, cy, 16, 16)
        const img = ctx.getImageData(cx, cy, 16, 16)
        const d = img.data
        for (let p = 0; p < d.length; p += 4) {
            if (d[p + 3] < 128) continue
            const yy = ((p / 4) / 16) | 0 // row within this 16px frame
            const k = `${d[p]},${d[p + 1]},${d[p + 2]}`
            let out: RGB | null = null
            if (k in SHIRT) out = SHIRT[k]
            else if (k in SKIN) out = SKIN[k]
            else if (yy < HAIR_ROWS && k in HAIR) out = HAIR[k]
            else if (yy >= HAIR_ROWS && yy <= 12 && k === SKIN_SHADOW_SRC) out = skinSh
            if (out) { d[p] = out[0]; d[p + 1] = out[1]; d[p + 2] = out[2] }
        }
        ctx.putImageData(img, cx, cy)
    }

    const key = bodyKey(id)
    if (scene.textures.exists(key)) scene.textures.remove(key)
    const ct = scene.textures.addCanvas(key, canvas)!
    ct.setFilter(Phaser.Textures.FilterMode.NEAREST) // crisp pixels, no edge interpolation
    for (let idx = 0; idx < 12; idx++) { const col = idx % 3, row = (idx / 3) | 0; ct.add(idx, 0, col * STRIDE, row * STRIDE, 16, 16) }
    return key
}
